import { headlessGet } from './api';
import {
  THEME_PRESETS,
  HEADING_WEIGHT_MAP,
  LETTER_SPACING_MAP,
  SHADOW_STYLE_MAP,
  type ThemePreset,
} from './theme-presets';

export interface SiteBranding {
  name: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  faviconUrl: string | null;
}

export interface HeadlessSettings extends SiteBranding {
  isPrivate: boolean;
  homepageSlug: string | null;
  theme: Partial<Record<string, string>>;
  layout: Partial<Record<string, string>>;
  trackingWriteKey: string | null;
  customDomain: string | null;
  redirectMode: 'none' | 'to_custom' | 'to_default';
}

export const BRANDING_DEFAULTS: HeadlessSettings = {
  name: 'Website',
  logoUrl: null,
  primaryColor: '#4F46E5',
  secondaryColor: '#0F172A',
  fontFamily: 'Inter',
  faviconUrl: null,
  isPrivate: false,
  homepageSlug: null,
  theme: {},
  layout: {},
  trackingWriteKey: null,
  customDomain: null,
  redirectMode: 'none',
};

export const SUPPORTED_FONTS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Poppins',
  'Montserrat',
  'Nunito',
  'Raleway',
  'Source Sans 3',
  'Work Sans',
  'DM Sans',
  'Plus Jakarta Sans',
] as const;

export type SupportedFont = (typeof SUPPORTED_FONTS)[number];

function loadGoogleFont(family: string): void {
  if (family === 'Inter') return;
  const id = `gf-${family.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  const encoded = encodeURIComponent(family);
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

const RADIUS_MAP: Record<string, string> = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '16px',
  full: '9999px',
};

/**
 * Fetches all space settings in a single call from /api/headless/v1/settings.
 * Returns branding, theme, layout config, isPrivate and homepageSlug.
 */
export async function fetchHeadlessSettings(): Promise<HeadlessSettings> {
  try {
    const data = await headlessGet<Partial<HeadlessSettings>>('/settings');
    return {
      name: data.name || BRANDING_DEFAULTS.name,
      logoUrl: data.logoUrl || null,
      primaryColor: data.primaryColor || BRANDING_DEFAULTS.primaryColor,
      secondaryColor: data.secondaryColor || BRANDING_DEFAULTS.secondaryColor,
      fontFamily: data.fontFamily || BRANDING_DEFAULTS.fontFamily,
      faviconUrl: data.faviconUrl || null,
      isPrivate: data.isPrivate ?? false,
      homepageSlug: data.homepageSlug ?? null,
      theme: (data.theme as Partial<Record<string, string>>) ?? {},
      layout: (data.layout as Partial<Record<string, string>>) ?? {},
      trackingWriteKey: (data.trackingWriteKey as string | null | undefined) ?? null,
      customDomain: (data as Record<string, unknown>).customDomain as string | null ?? null,
      redirectMode: ((data as Record<string, unknown>).redirectMode as HeadlessSettings['redirectMode']) ?? 'none',
    };
  } catch {
    return BRANDING_DEFAULTS;
  }
}

export function applyBranding(settings: HeadlessSettings): void {
  const root = document.documentElement;
  const theme = settings.theme;

  // If a preset is specified, merge its values as defaults beneath explicit overrides
  const presetName = theme.preset as ThemePreset | undefined;
  const presetDefaults = presetName ? THEME_PRESETS[presetName] : undefined;

  function resolve(key: string): string | undefined {
    return theme[key] || (presetDefaults as Record<string, unknown> | undefined)?.[key] as string | undefined;
  }

  function setOrRemove(prop: string, value: string | undefined) {
    if (value) root.style.setProperty(prop, value);
    else root.style.removeProperty(prop);
  }

  // Set preset attribute for CSS selectors
  if (presetName && presetName !== 'custom') {
    root.setAttribute('data-theme-preset', presetName);
  } else {
    root.removeAttribute('data-theme-preset');
  }

  // Core colors
  root.style.setProperty('--color-primary', resolve('primaryColor') ?? settings.primaryColor);
  root.style.setProperty('--color-secondary', resolve('secondaryColor') ?? settings.secondaryColor);
  setOrRemove('--color-accent', resolve('accentColor'));
  setOrRemove('--color-bg', resolve('backgroundColor'));

  // Extended surface colors
  setOrRemove('--color-surface', resolve('surfaceColor'));
  setOrRemove('--color-surface-border', resolve('surfaceBorderColor'));
  setOrRemove('--color-text', resolve('textColor'));
  setOrRemove('--color-text-muted', resolve('textMutedColor'));

  // Typography — body font
  const fontFamily = resolve('fontFamily') ?? settings.fontFamily;
  loadGoogleFont(fontFamily);
  root.style.setProperty('--font-family', `"${fontFamily}", Inter, system-ui, sans-serif`);

  // Typography — heading font (may differ from body)
  const headingFont = resolve('fontFamilyHeading');
  if (headingFont && headingFont !== fontFamily) {
    loadGoogleFont(headingFont);
    root.style.setProperty('--font-heading', `"${headingFont}", Inter, system-ui, sans-serif`);
  } else {
    root.style.removeProperty('--font-heading');
  }

  // Typography — heading style
  const hw = resolve('headingWeight');
  setOrRemove('--heading-weight', hw ? (HEADING_WEIGHT_MAP[hw] ?? hw) : undefined);
  setOrRemove('--heading-style', resolve('headingStyle'));
  setOrRemove('--heading-transform', resolve('headingTransform'));
  const ls = resolve('letterSpacing');
  setOrRemove('--letter-spacing', ls ? (LETTER_SPACING_MAP[ls] ?? ls) : undefined);

  // Border radius
  const br = resolve('borderRadius');
  if (br) {
    root.style.setProperty('--radius', RADIUS_MAP[br] ?? br);
  } else {
    root.style.removeProperty('--radius');
  }

  // Effects
  const ss = resolve('shadowStyle');
  setOrRemove('--shadow-style', ss ? (SHADOW_STYLE_MAP[ss] ?? ss) : undefined);
  setOrRemove('--glow-color', resolve('glowColor'));
  setOrRemove('--pattern-bg', resolve('patternBackground'));

  document.title = settings.name;

  if (settings.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = settings.faviconUrl;
  }
}
